import { IconFromSet } from "@gettactic/components";
import React from "react";
import ReactDOMServer from "react-dom/server";
import { Group, Image, Text } from "react-konva";
import { IconShape as IIconShape } from "../../../../types";
import { mergeRefs } from "../../../../utils/mergeRef";
import {
  loadColoredSvgMarkupImage,
  loadColoredSvgUrlImage
} from "../../../../utils/svgAssetCache";
import { getTextProps } from "../../helpers/getTextProps";
import { getWindowKonvaObject } from "../../helpers/konva/getWindowKonvaObject";
import { ShapeCommonProps } from "../../types";
import { useShape } from "../ShapeContext/useShape";
import { IconShapeSVG } from "./IconShapeSVG";

export interface IconShapeComponentProps extends ShapeCommonProps {
  renderMode?: "konva" | "svg";
}

export const IconShape = React.forwardRef<
  React.ComponentRef<typeof Group>,
  IconShapeComponentProps
>(({ renderMode = "konva", ...commonProps }, ref) => {
  const { isHovered, shape, isInSelection, image, setImage } = useShape();

  const iconShape = shape as IIconShape;
  const shapeRef = React.useRef<React.ComponentRef<typeof Group>>(null);

  React.useEffect(() => {
    if (shape.type !== "icon") return;
    let cancelled = false;

    const applyImage = (img: HTMLImageElement) => {
      if (cancelled) return;
      setImage(img);
      getWindowKonvaObject().stages?.[0]?.fire?.("contentChange");
    };

    // All three sources go through the shared session cache: identical
    // icons (same markup/url + color) share a single fetch and a single
    // rasterized image element instead of one per shape.

    // Prefer the trimmed svgContent baked in at placement time — its
    // viewBox hugs the actual icon content, so when Konva rasterizes it
    // there's no surrounding whitespace inside the shape's bounding box.
    if (iconShape.svgContent) {
      loadColoredSvgMarkupImage(
        iconShape.svgContent,
        shape.color || "currentColor"
      ).then(applyImage);
    } else if (iconShape.iconUrl) {
      // API-sourced icon
      loadColoredSvgUrlImage(
        iconShape.iconUrl,
        shape.color || "currentColor"
      ).then(applyImage);
    } else if (shape.icon) {
      const svgString = ReactDOMServer.renderToStaticMarkup(
        <IconFromSet icon={shape.icon} color={shape.color} size={64} />
      );
      // Color is already baked into the markup by IconFromSet.
      loadColoredSvgMarkupImage(svgString, "currentColor").then(applyImage);
    }

    return () => {
      cancelled = true;
    };
  }, [shape.type, (shape as IIconShape).icon, (shape as IIconShape).iconUrl, (shape as IIconShape).svgContent, shape.color]);

  React.useEffect(() => {
    if (shape.type === "icon" && image) {
      const layer = shapeRef.current?.getLayer();
      if (layer) {
        layer.batchDraw();
      }
    }
  }, [shape.type, image]);

  // If renderMode is SVG, return SVG components
  if (renderMode === "svg") {
    return <IconShapeSVG />;
  }

  // Default Konva rendering
  return (
    <Group
      width={shape.width}
      height={shape.height}
      ref={mergeRefs([shapeRef, ref])}
      {...commonProps}
    >
      <Image
        image={image || undefined}
        width={shape.width}
        height={shape.height}
        opacity={shape.locked ? 0.5 : 1}
        imageSmoothingEnabled={true}
        // Perfect draw double-buffers every image draw — pure cost for
        // plain images, and maps render hundreds of icons.
        perfectDrawEnabled={false}
      />
      {iconShape.text && (
        <Text
          text={iconShape.text}
          y={iconShape.height + 10}
          x={0}
          ref={(node) => {
            if (node) {
              const textWidth = node.width();
              if (iconShape.textAlign === "center") {
                node.x((iconShape.width - textWidth) / 2);
              } else if (iconShape.textAlign === "right") {
                node.x(iconShape.width - textWidth);
              } else {
                node.x(0);
              }
            }
          }}
          {...getTextProps(iconShape)}
          wrap={"none"}
        />
      )}
    </Group>
  );
});
